/**
 * Per-unit variant choice inside a quantity offer (28/09/2026 — the variants gap).
 *
 * Every quantity-breaks app the dropshippers use (Kaching, Pumper, WideBundle, Fly — see
 * `docs/features/0069-quantity-offer-templates-references-2026-09-28.png`) lets the buyer pick a variant
 * FOR EACH UNIT of the pack: inside the checked tile, one row per unit — «#1», «#2», «#3» — with a dropdown
 * per option (Size, Colour). Ours used to sell a pack of three as three copies of the one variant the page
 * picker held, which is not how anyone buys three t-shirts.
 *
 * This module is the pure part: how a product's option matrix maps onto its flat variant list (labels are
 * the option values joined with « / », exactly as the backend and the variant-picker block read them), and
 * how a list of per-unit variant ids becomes the `(variantId, quantity)` lines the checkout, the cart quote
 * and the promo preview all take. No React, no DOM — unit-tested on its own.
 */
import { assetVariantUrl } from './assetVariant';
/** The canonical `StorefrontVariant.label` join («Sky Blue / Standard») — one value per option. */
export const OPTION_SEP = ' / ';
/**
 * The ONE place the three cases are decided. Everything downstream — the dots, the leading thumbnail, whether
 * a photo column exists at all — reads the matrix, so applying the mode here means no other caller can forget
 * a case. In `custom` the product's own photo is deliberately NOT a fallback: it is the thing she found
 * unreadable at this size, so a value she has not uploaded wears its colour instead.
 */
function unitPhotoPicker(images) {
    const mode = images?.mode === 'custom' || images?.mode === 'none' ? images.mode : 'variant';
    if (mode === 'none')
        return () => null;
    if (mode === 'variant')
        return (_label, own) => (own ? unitRendition(own) : null);
    const custom = new Map();
    for (const p of images?.photos ?? []) {
        const key = p.value.trim().toLowerCase();
        // The FIRST photo for a value wins, like the seller's own image axis — a duplicate is not an error.
        if (key && p.imageUrl && !custom.has(key))
            custom.set(key, p.imageUrl);
    }
    return (label) => {
        const hit = custom.get(label.trim().toLowerCase());
        return hit ? unitRendition(hit) : null;
    };
}
/** The smallest rung our asset service cuts; a 34 px box on a 3× screen is 102 px, so it is ample. */
const SMALL_RENDITION_WIDTH = 320;
/**
 * The SMALL rendition of a photo, for a dot of 22 px and a thumbnail of 34 px (29/09/2026).
 *
 * The founder's own uploads came back as torn-page icons, and the reason was not the url: the page was
 * asking for the FULL file — 187 to 290 KB of PNG — to paint a 22 px circle, three times over. The 320 px
 * rendition of the SAME image weighs 16 KB. On the shop's real network that is the difference between a
 * swatch that paints and one that does not. A foreign url (not one of ours) comes back untouched.
 */
function unitRendition(url) {
    return assetVariantUrl(url, SMALL_RENDITION_WIDTH);
}
/**
 * The option matrix behind a variant list, when the list IS a matrix: at least one product option, and every
 * variant label the canonical join of one KNOWN value per option. Anything else (odd labels, the synthetic
 * first-option-only fallback with a differing count, a product with no options) answers null and the row
 * shows ONE dropdown listing the variants themselves — same rule as the variant-picker's multi-option path.
 */
export function optionMatrix(options, variants, images) {
    if (!options || options.length === 0 || variants.length === 0)
        return null;
    const photo = unitPhotoPicker(images);
    const rows = options.map((o) => {
        const values = o.values.map((v) => ({
            label: v.label,
            swatch: v.swatch ?? null,
            imageUrl: photo(v.label, v.imageUrl ?? null),
        }));
        return {
            name: o.name,
            values,
            swatches: values.length > 0 && values.every((v) => !!v.swatch || !!v.imageUrl),
        };
    });
    const canonical = variants.every((v) => {
        const parts = v.label.split(OPTION_SEP);
        return (parts.length === rows.length &&
            parts.every((p, i) => rows[i].values.some((val) => val.label === p)));
    });
    return canonical ? rows : null;
}
/** The option values of a variant, in option order. */
export function optionParts(label) {
    return label.split(OPTION_SEP);
}
/**
 * The variant to select when row [rowIdx] of the unit currently on [current] is changed to [value]: the
 * exact combination when the matrix has it, else the nearest variant carrying that value (an available one
 * first) so every listed value stays reachable — the variant-picker's own rule, so both selectors agree.
 */
export function variantForChoice(variants, current, rowIdx, value) {
    const parts = [...optionParts(current.label)];
    parts[rowIdx] = value;
    const exact = variants.find((v) => v.label === parts.join(OPTION_SEP));
    if (exact)
        return exact;
    const carrying = variants.filter((v) => optionParts(v.label)[rowIdx] === value);
    return carrying.find((v) => v.available) ?? carrying[0];
}
/**
 * The photo to paint at the head of a unit's row (28/09/2026 — Fly Bundles and Easy Bundles both put the
 * VARIANT's image before «#1», so the buyer reads the row as «this unit, this thing» rather than as a pair
 * of dropdowns). Our option values already carry `imageUrl` (the backend derives it from the images the
 * seller tagged with that value), so the variant's photo is the first tagged value's photo, in option
 * order: for «M / Sky Blue» that is the Colour photo, since a size carries none.
 *
 * Answers null when no value of that combination carries one — the caller then falls back to the product's
 * own cover, or paints no column at all when NOTHING in the matrix has a photo (three identical covers
 * stacked under a tile say nothing and only make the pack taller).
 */
export function unitImageUrl(rows, label) {
    if (!rows)
        return null;
    const parts = optionParts(label);
    for (const [i, row] of rows.entries()) {
        const hit = row.values.find((v) => v.label === parts[i]);
        if (hit?.imageUrl)
            return hit.imageUrl;
    }
    return null;
}
/** True when this matrix has at least one value photo — i.e. the unit rows have a thumbnail column to show. */
export function matrixHasPhotos(rows) {
    return !!rows && rows.some((r) => r.values.some((v) => !!v.imageUrl));
}
/**
 * Group per-unit variant ids into lines, first-seen order, empty ids dropped. `['a','b','a']` →
 * `[{a,2},{b,1}]`. A pack whose units are all one variant is exactly the one line the body always carried.
 */
export function groupUnitLines(unitVariantIds) {
    const out = [];
    for (const id of unitVariantIds) {
        if (!id)
            continue;
        const hit = out.find((l) => l.variantId === id);
        if (hit)
            hit.quantity += 1;
        else
            out.push({ variantId: id, quantity: 1 });
    }
    return out;
}
//# sourceMappingURL=unitVariants.js.map